import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { mediaList } from "@/mocks/media";
import { filterMedia, type SortKey } from "@/utils/media";

const TYPE_TABS = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

const SORT_TABS: { key: SortKey; label: string }[] = [
  { key: "popularity", label: "相关度 / 热度" },
  { key: "rating", label: "评分最高" },
  { key: "latest", label: "最新上线" },
];

const HOT_KEYWORDS = ["科幻", "悬疑", "爱情", "历史", "都市"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKeyword = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(urlKeyword);
  const [type, setType] = useState<"all" | "movie" | "tv">("all");
  const [sort, setSort] = useState<SortKey>("popularity");

  useEffect(() => {
    setKeyword(urlKeyword);
  }, [urlKeyword]);

  usePageMeta({
    title: urlKeyword
      ? `“${urlKeyword}” 搜索结果 - 聚会的目的在线`
      : "搜索影视作品 - 聚会的目的在线",
    description: urlKeyword
      ? `与“${urlKeyword}”相关的电影、电视剧搜索结果，包含剧情介绍与演员信息，方便你快速找到想看的作品。`
      : "在聚会的目的在线搜索电影与电视剧，支持按名称、类型、演员等关键词快速查找影视资源。",
    keywords: "影视搜索,电影搜索,电视剧搜索,聚会的目的在线",
  });

  const results = useMemo(
    () =>
      filterMedia(mediaList, {
        keyword,
        type: type === "all" ? undefined : type,
        sort,
      }),
    [keyword, type, sort]
  );

  const hasKeyword = keyword.trim().length > 0;

  const applyKeyword = (kw: string) => {
    setKeyword(kw);
    const params = new URLSearchParams();
    if (kw.trim()) params.set("q", kw.trim());
    setSearchParams(params, { replace: true });
  };

  return (
    <Layout>
      <div className="w-full bg-primary-900 text-background-100">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-white">搜索影视作品</h1>
          <p className="mt-3 text-sm md:text-base text-primary-100 max-w-2xl">
            输入片名、类型或演员关键词，即可快速找到相关电影与电视剧。
          </p>

          <div className="mt-6 max-w-2xl">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(e) => applyKeyword(e.target.value)}
                placeholder="例如：星海归途、悬疑、沈砚…"
                className="w-full h-12 md:h-14 pl-11 pr-4 text-sm md:text-base rounded-md bg-background-50 text-foreground-900 border border-transparent focus:border-accent-400 outline-none"
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-primary-200">热门搜索：</span>
              {HOT_KEYWORDS.map((kw) => (
                <button
                  key={kw}
                  type="button"
                  onClick={() => applyKeyword(kw)}
                  className="px-2.5 py-1 rounded-md bg-primary-800 text-background-100 text-xs hover:bg-primary-700 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  {kw}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        {hasKeyword && (
          <p className="text-sm text-foreground-600 mb-5">
            关键词 <span className="text-primary-600 font-medium">“{keyword}”</span> 共匹配到
            <span className="text-accent-600 font-semibold mx-1">{results.length}</span>部影视作品
          </p>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-7">
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setType(tab.key as typeof type)}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  type === tab.key
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground-500">排序：</span>
            {SORT_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSort(tab.key)}
                className={`px-3 py-1.5 rounded-md text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === tab.key
                    ? "bg-accent-500 text-background-50 font-medium"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {results.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
            {results.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        ) : hasKeyword ? (
          <EmptyState
            icon="ri-search-eye-line"
            title={`没有找到与“${keyword}”匹配的作品`}
            description="试试更换关键词，可能是片名输入有误，或尝试使用类型、演员等其他关键词搜索。"
          />
        ) : (
          <EmptyState
            icon="ri-search-line"
            title="开始搜索你感兴趣的影视作品"
            description="在上方输入框输入片名、类型或演员关键词，例如“科幻”“悬疑”“沈砚”等，即可查看相关结果。"
          />
        )}

        {hasKeyword && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">推荐搜索：</span>
            {HOT_KEYWORDS.map((kw) => (
              <Link
                key={kw}
                to={`/search?q=${encodeURIComponent(kw)}`}
                className="px-3 py-1.5 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {kw}
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}